import React, { useEffect } from 'react'
import { toast } from 'react-toastify'
import { Box, Chip, Grid, MenuItem, Select, TextField, Typography } from '@mui/material'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { useMutation } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import CatalogAPI from 'api/faber/CatalogAPI'
import NormalText from 'common/Typography/NormalText'
import { allCountries } from 'constants/supportedCountriesList'
import { Form, Formik } from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as Yup from 'yup'

const CustomDatePicker = (props: DatePickerProps) => {
  const { label, disabled, date, setDate } = props

  const handleStartDate = (newValue: Date | null) => {
    setDate(newValue)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DateTimePicker
        label={label}
        inputFormat='dd-MM-yyyy HH:00'
        views={['year', 'month', 'day', 'hours']}
        value={date}
        onChange={handleStartDate}
        renderInput={(params) => (
          <TextField placeholder='Date' variant='standard' {...params} />
        )}
        disabled={disabled}
      />
    </LocalizationProvider>
  )
}

export default CustomDatePicker

interface DatePickerProps {
  label: string
  disabled: boolean
  date: Date | null
  setDate: (value: Date | null) => void
}

const formikInitialValues = {
  packageTypeRef: '',
  vendor: 'TELNA',
  tenant: 'USA',
  name: '',
  description: '',
  callout: '',
  currency: '',
  value: '',
  symbol: '',
  dataInGB: 0,
  voiceInMins: 0,
  SMS: 0,
  validityInDays: 0,
  image: '',
  countries: [] as string[],
  tags: [] as string[],
  packageType: 'BASE',
  visibility: false,
  supports5g: false,
  redirectUrl: '',
  provisionMethodFKId: '1'
}

const NewCatalogSchema = Yup.object().shape({})

const StyledTextField = styled(TextField)({
  '& input': {
    padding: '6px'
  },
  width: '100%'
})

const startDate = new Date()
startDate.setMinutes(0)

const endDate = new Date()
endDate.setMinutes(0)

export const NewCatalogForm = ({
  handleClose
}: {
  handleClose: () => void
}) => {
  interface CatalogRow {
    id: string
    stringId?: string
    packageTypeId?: string | number
    reward_name: string
    reward_description: string
    reward_description_internal: string
  }
  interface ProvisionRow {
    id: string
    method: string
    esimConfigId: string
  }

  const [packageRows, setPackageRows] = React.useState<CatalogRow[]>([])
  const [packageSelectionModel, setPackageSelectionModel] =
    React.useState<string>('')
  const [tagInput, setTagInput] = React.useState('')
  const [countryInput, setCountryInput] = React.useState('')
  const [provisionMethodRows, setProvisionMethodRows] = React.useState<ProvisionRow[]>([])
  const handleUsageTypeSelect = (
    e,
    setFieldValue: (
      field: string,
      value,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    setFieldValue(e.target.name, e.target.value)
  }
  const faberToken = OrbitSession.get('faberToken')

  const { data: packageItems } = useQuery(['getTelnaPackages'], async () =>
    CatalogAPI.getAllTelnaPackages(faberToken)
  )

  const packageColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 200
    },
    {
      field: 'dataUsageAllowanceInBytes',
      headerName: 'Data In Bytes',
      width: 200
    },
    {
      field: 'notes',
      headerName: 'notes',
      width: 350
    },
    {
      field: 'supportedCountries',
      headerName: 'Supported Countries',
      width: 350
    },
  ]

  const { data: provisionMethods } = useQuery(['getProvisionMethods'], async () =>
    CatalogAPI.getAllProvisionMethods(faberToken)
  )
  useEffect(() => {
    if (provisionMethods?.data) {
      setProvisionMethodRows(provisionMethods.data);
    }
  }, [provisionMethods]);

  useEffect(() => {
    if (packageItems) {
      setPackageRows(packageItems?.data)
    }
  }, [packageItems])

  const { mutate: createCatalog } = useMutation(CatalogAPI.createCatalogItem, {
    onSuccess: () => {
      toast.success('Catalog created successfully')
      handleClose()
    },
    onError: () => {
      toast.error('Failed to create catalog')
    }
  })

  const isValidCountryCode = (code: string) =>
    /^[A-Z]{2,3}$/.test(code) || Object.values(allCountries).includes(code)


  const handleFormSubmit = (values: NewCatalogFormikProps) => {
    const supportedCountries = values.countries
      .map((code) => code.trim().toUpperCase())
      .filter((code) => isValidCountryCode(code))

    if (supportedCountries.length !== values.countries.length) {
      toast.error('Invalid countries')
      return
    }

    const selectedPackage = packageRows.find(
      (pkg) =>
        String(pkg.id) === String(packageSelectionModel) ||
        String(pkg.stringId || '') === String(packageSelectionModel)
    )
    const packageTypeId = selectedPackage?.packageTypeId
    if (packageSelectionModel && (packageTypeId === undefined || packageTypeId === null || packageTypeId === '')) {
      toast.error('Selected package does not have packageTypeId')
      return
    }

    const allValues = {
      vendor: values.vendor,
      tenant: values.tenant,
      packageType: values.packageType,
      packageTypeId: packageTypeId !== undefined && packageTypeId !== null ? String(packageTypeId) : '',
      name: values.name,
      description: [values.description],
      callout: values.callout,
      dataInGB: Number(values.dataInGB),
      voiceInMins: String(values.voiceInMins),
      sms: String(values.SMS),
      validityInDays: String(values.validityInDays),
      countries: supportedCountries,
      visibility: values.visibility,
      supports5g: values.supports5g,
      tags: values.tags,
      redirectUrl: values.redirectUrl,
      faberToken: faberToken,
      provisionMethodFk: values.provisionMethodFKId || '',
      vendorPackageId: selectedPackage ? String(selectedPackage.id || selectedPackage.stringId || '') : ''
    }

    createCatalog(allValues)
  }

  /**
   * Event handlers
   */
  return (
    <Formik
      initialValues={formikInitialValues}
      onSubmit={handleFormSubmit}
      validationSchema={NewCatalogSchema}
    >
      {({
        submitForm,
        handleChange,
        errors,
        touched,
        handleBlur,
        setFieldValue,
        values
      }) => (
        <Form>
          <Grid container rowSpacing={2}>
            {/* CAMPAIGN PROMO NAME */}
            <Grid item xs={3}>
              <NormalText isBold>Name</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='name'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'name'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.name && touched.name)}
                  helperText={errors.name && touched.name ? errors.name : null}
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Description</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='description'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'description'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.description && touched.description)}
                  helperText={
                    errors.description && touched.description
                      ? errors.description
                      : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Currency</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='currency'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'currency'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.currency && touched.currency)}
                  helperText={
                    errors.currency && touched.currency ? errors.currency : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Value</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='value'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'value'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.value && touched.value)}
                  helperText={
                    errors.value && touched.value ? errors.value : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Symbol</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='symbol'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'symbol'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.symbol && touched.symbol)}
                  helperText={
                    errors.symbol && touched.symbol ? errors.symbol : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Data In GB</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='dataInGB'
                  type='number'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'dataInGB'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.dataInGB && touched.dataInGB)}
                  helperText={
                    errors.dataInGB && touched.dataInGB ? errors.dataInGB : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Validity In Days</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='validityInDays'
                  type='number'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'validityInDays'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.validityInDays && touched.validityInDays)}
                  helperText={
                    errors.validityInDays && touched.validityInDays
                      ? errors.validityInDays
                      : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Image URL (Link from S3)</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='image'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'image'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.image && touched.image)}
                  helperText={
                    errors.image && touched.image ? errors.image : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Countries</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: 1,
                    alignItems: 'center'
                  }}
                >
                  <StyledTextField
                    name='countryInput'
                    value={countryInput}
                    onChange={(e) => setCountryInput(e.target.value.toUpperCase())}
                    inputProps={{
                      'data-testid': 'countries-input'
                    }}
                    fullWidth
                    variant='outlined'
                    placeholder='Add country code (e.g. IND)'
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        const nextCountry = countryInput.trim().toUpperCase()
                        if (!nextCountry || values.countries.includes(nextCountry)) {
                          return
                        }
                        setFieldValue('countries', [...values.countries, nextCountry])
                        setCountryInput('')
                      }
                    }}
                  />
                  <Button
                    variant='contained'
                    sx={{ minWidth: 92, height: 40 }}
                    onClick={() => {
                      const nextCountry = countryInput.trim().toUpperCase()
                      if (!nextCountry || values.countries.includes(nextCountry)) {
                        return
                      }
                      setFieldValue('countries', [...values.countries, nextCountry])
                      setCountryInput('')
                    }}
                    disabled={!countryInput.trim()}
                  >
                    Add
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: 36 }}>
                  {values.countries.length > 0 ? (
                    values.countries.map((country) => (
                      <Chip
                        key={country}
                        label={country}
                        color={isValidCountryCode(country) ? 'primary' : 'error'}
                        variant='outlined'
                        onDelete={() =>
                          setFieldValue(
                            'countries',
                            values.countries.filter((existingCountry) => existingCountry !== country)
                          )
                        }
                      />
                    ))
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      No countries added.
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Tenant</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='tenant'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'tenant'
                  }}
                  defaultValue='USA'
                  fullWidth
                  variant='outlined'
                  error={!!(errors.tenant && touched.tenant)}
                  helperText={
                    errors.tenant && touched.tenant ? errors.tenant : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Tags</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: 1,
                    alignItems: 'center'
                  }}
                >
                  <StyledTextField
                    name='tagInput'
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    inputProps={{
                      'data-testid': 'tags-input'
                    }}
                    fullWidth
                    variant='outlined'
                    placeholder='Add tag'
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        const nextTag = tagInput.trim()
                        if (!nextTag || values.tags.includes(nextTag)) {
                          return
                        }
                        setFieldValue('tags', [...values.tags, nextTag])
                        setTagInput('')
                      }
                    }}
                  />
                  <Button
                    variant='contained'
                    sx={{ minWidth: 92, height: 40 }}
                    onClick={() => {
                      const nextTag = tagInput.trim()
                      if (!nextTag || values.tags.includes(nextTag)) {
                        return
                      }
                      setFieldValue('tags', [...values.tags, nextTag])
                      setTagInput('')
                    }}
                    disabled={!tagInput.trim()}
                  >
                    Add
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: 36 }}>
                  {values.tags.length > 0 ? (
                    values.tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        color='primary'
                        variant='outlined'
                        onDelete={() =>
                          setFieldValue(
                            'tags',
                            values.tags.filter((existingTag) => existingTag !== tag)
                          )
                        }
                      />
                    ))
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      No tags added.
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Package Type</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Select
                variant='standard'
                sx={{ width: '80%' }}
                onChange={(e) => handleUsageTypeSelect(e, setFieldValue)}
                name='packageType'
                defaultValue='BASE'
              >
                <MenuItem value='BASE'>Base</MenuItem>
                <MenuItem value='BASE_UNLIMITED'>BASE_UNLIMITED</MenuItem>
                <MenuItem value='BONUS'>Bonus</MenuItem>
                <MenuItem value='PROMO'>Promo</MenuItem>
                <MenuItem value='TOPUP'>Topup</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Vendor</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Select
                variant='standard'
                sx={{ width: '80%' }}
                onChange={(e) => handleUsageTypeSelect(e, setFieldValue)}
                name='vendor'
                defaultValue='TELNA'
              >
                <MenuItem value='TELNA'>TELNA</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Provision Method</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Select
                variant='standard'
                sx={{ width: '80%' }}
                onChange={(e) => handleUsageTypeSelect(e, setFieldValue)}
                name='provisionMethodFKId'
                defaultValue={provisionMethodRows && provisionMethodRows.length > 0 ? provisionMethodRows[0].id : '1'}
              >
                {provisionMethodRows && provisionMethodRows.map((provisionMethod) => {
                  return <MenuItem key={provisionMethod.id} value={provisionMethod.id}>{provisionMethod.method}</MenuItem>
                })}
              </Select>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Underlying Package</NormalText>
            </Grid>
            <Grid item xs={12} height={640}>
              <DataGrid
                rows={packageRows}
                columns={packageColumns}
                rowsPerPageOptions={[10]}
                pageSize={10}
                selectionModel={packageSelectionModel ? [packageSelectionModel] : []}
                getRowId={(row) => row.id || row.stringId}
                onSelectionModelChange={(newSelection) => {
                  setPackageSelectionModel(newSelection[0] as string)
                }}
              />
            </Grid>
          </Grid>
          <Grid>
            <Box mt={3}>
              {' '}
              {/* Adjust the number to increase or decrease the space */}
              <Button onClick={submitForm}>Create catalog</Button>
            </Box>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}

interface NewCatalogFormikProps {
  packageTypeRef: string
  vendor: string
  tenant: string
  name: string
  description: string
  callout?: string
  currency: string
  value: string
  symbol: string
  dataInGB: number
  voiceInMins: number
  SMS: number
  validityInDays: number
  image?: string
  countries: string[]
  tags: string[]
  packageType: string
  visibility: boolean
  supports5g: boolean
  redirectUrl?: string
  provisionMethodFKId?: string
}
